import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import DrLicitaConfirm from 'components/molecules/dr_licita_confirm';
import AmbassadorFeedbackBar from 'components/molecules/ambassador_feedback_bar';
import { AMBASSADOR_FEEDBACK_BAR_HEIGHT } from 'components/molecules/ambassador_feedback_bar/styles';
import { isAmbassadorTesting } from 'services/ambassador';
import { DR_LICITA_AMBASSADOR_SLUG, findAmbassadorTool } from 'services/ambassador_tools';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';

import {
  CanvasDocument,
  createDrLicitaChat,
  deleteDrLicitaChat,
  DraftPayload,
  DrLicitaChatMessage,
  DrLicitaChatSummary,
  fetchCanvas,
  fetchDrLicitaChatMessages,
  fetchDrLicitaChats,
  fetchTurnoAtendimento,
  OpcoesPayload,
  pararTurnoAtendimento,
  PecaSalvaPayload,
  rateDrLicitaChatMessage,
  renameDrLicitaChat,
  sendDrLicitaChatMessage,
  streamDrLicitaChatMessage,
  tituloConversa,
  uploadDrLicitaChatAttachment,
} from 'services/actions/dr_licita_chat_actions';

import {
  DrLicitaProjeto,
  fetchDrLicitaProjetos,
  setChatProjeto,
} from 'services/actions/dr_licita_projetos_actions';
import { refreshProfile } from 'store/modules/profile_store/actions';

import * as PS from '../DrLicitaPeticoes/styles';
import CanvasPanel, { CanvasLive, CanvasPanelHandle } from './CanvasPanel';
import * as C from './canvas_styles';
import * as S from './styles';

const TOOL_STATUS: Record<string, string> = {
  listar_temas: 'Consultando o catálogo de petições…',
  listar_legislacoes: 'Consultando as legislações…',
  buscar_teses: 'Buscando teses aplicáveis ao seu caso…',
  redigir_peca: 'Redigindo a petição e verificando as citações (pode levar um minuto)…',
  editar_peca: 'Revisando a petição e verificando as citações…',
  salvar_peca: 'Salvando em Petições…',
  consultar_acervo: 'Consultando o acervo jurídico…',
  consultar_acervo_drlicita: 'Consultando o acervo de teses do Dr. Licita…',
  consultar_licitacao_conlicitacao: 'Buscando a licitação na ConLicitação…',
  consultar_jurisprudencia: 'Consultando a jurisprudência…',
};

// Modo Atendimento: o formato do IAGov (conversa livre + teses + petição
// escrita ao vivo no painel ao lado do chat)
const MODO_ATENDIMENTO = 'atendimento';
const FALHA_ATENDIMENTO =
  'Não consegui concluir essa resposta agora. Pode enviar de novo, por favor?';
const ESPERANDO_TURNO = 'O Dr. Licita ainda está terminando esta resposta…';

// Atalhos da conversa nova do Atendimento: casos comuns (dos roteiros do
// Construir Petição) que preenchem o campo com um texto editável
const CASOS_COMUNS = [
  {
    rotulo: 'Fui inabilitado no pregão',
    texto:
      'Minha empresa foi inabilitada no pregão [número] de [órgão], em [data], porque [motivo da inabilitação]. Quero recorrer para ser habilitada.',
  },
  {
    rotulo: 'Exigência restritiva no edital',
    texto:
      'O edital do pregão [número] de [órgão] exige [exigência] no item [item], o que restringe a competição. A sessão é em [data]. Quero impugnar.',
  },
  {
    rotulo: 'Recurso de concorrente contra mim',
    texto:
      'Um concorrente recorreu contra a minha habilitação no pregão [número] de [órgão], alegando [argumento do recurso]. Quero apresentar contrarrazões.',
  },
  {
    rotulo: 'Pagamento em atraso',
    texto:
      'O órgão [órgão] não pagou as notas fiscais [números] do contrato [número], vencidas em [datas]. Quero cobrar o pagamento.',
  },
  {
    rotulo: 'Notificação de penalidade',
    texto:
      'Recebi uma notificação de [órgão] para aplicação de [penalidade] no contrato [número], por [motivo]. O prazo da defesa vence em [data]. Quero me defender.',
  },
  {
    rotulo: 'Reequilíbrio do contrato',
    texto:
      'Os custos do contrato [número] com [órgão] subiram por [evento] desde [data]. Quero pedir o reequilíbrio econômico-financeiro.',
  },
];

// Caso montado no Resumo IA da licitação (botões "Impugnar este item" e
// "Pedir esclarecimento"): vem por localStorage porque o botão abre outra aba
const lerCasoDoResumo = (chave: string): string | null => {
  try {
    const nome = `drlicita:caso:${chave}`;
    const texto = window.localStorage.getItem(nome);
    window.localStorage.removeItem(nome);
    return texto;
  } catch {
    return null;
  }
};

interface PassoDoTurno {
  label: string;
  detail?: string;
  done: boolean;
}

// Dr. Licita v3 — chat conversacional (rollout gradual: acessível por URL,
// sem entrada em menus; o wizard /dr_licita/document segue intacto)

// O Atendimento não tem card próprio: para quem o tem liberado, o Construir
// Petição (e cada tipo de petição) abre a conversa no formato Atendimento
const MODES = [
  {
    mode: 'peticao',
    title: 'Construir Petição',
    description: 'Crie sua petição administrativa com orientação passo a passo',
    icon: 'edit_note',
    disabled: false,
  },
  {
    mode: 'duvidas',
    title: 'Dúvidas Jurídicas',
    description: 'Receba orientações com base na legislação',
    icon: 'help_outline',
    disabled: false,
  },
  {
    mode: 'jurisprudencia',
    title: 'Jurisprudência Fácil',
    description: 'Pesquise dicas legais, jurisprudências e súmulas',
    icon: 'gavel',
    disabled: false,
  },
];

// Botões da tela Construir Petição — a chave casa com os roteiros do agente
const TIPOS_PECA = [
  {
    chave: 'IMPUGNACAO_EDITAL',
    titulo: 'Impugnação ao Edital',
    icon: 'rule',
    descricao: 'Corrigir ilegalidades, direcionamentos ou restrições à competitividade no edital',
  },
  {
    chave: 'RECURSO_ADMINISTRATIVO',
    titulo: 'Recurso Administrativo',
    icon: 'reply',
    descricao:
      'Reformar a decisão que inabilitou sua empresa ou classificou um concorrente irregular',
  },
  {
    chave: 'CONTRARRAZOES_RECURSO',
    titulo: 'Contrarrazões ao Recurso',
    icon: 'forum',
    descricao: 'Rebater o recurso de um concorrente e manter a decisão que beneficiou sua empresa',
  },
  {
    chave: 'COBRANCA_ADMINISTRATIVA',
    titulo: 'Cobrança Administrativa',
    icon: 'request_quote',
    descricao: 'Exigir o pagamento de faturas em atraso por serviços prestados ou bens entregues',
  },
  {
    chave: 'DENUNCIA_MP',
    titulo: 'Representação ao Ministério Público',
    icon: 'account_balance',
    descricao: 'Denunciar indícios de fraude, direcionamento grave ou improbidade em licitações',
  },
  {
    chave: 'DENUNCIA_TRIBUNAL_CONTAS',
    titulo: 'Representação ao Tribunal de Contas',
    icon: 'balance',
    descricao: 'Suspender licitação ilegal ou apurar dano ao erário (TCU ou TCE)',
  },
  {
    chave: 'DEFESA_PENALIDADE',
    titulo: 'Defesa Prévia de Penalidade',
    icon: 'shield',
    descricao: 'Evitar a aplicação de sanções após notificação ou autuação',
  },
  {
    chave: 'RECURSO_PENALIDADE',
    titulo: 'Recurso de Penalidade',
    icon: 'policy',
    descricao: 'Reformar a decisão que já aplicou uma penalidade à empresa',
  },
  {
    chave: 'REEQUILIBRIO_ECONOMICO_FINANCEIRO',
    titulo: 'Reequilíbrio Econômico-Financeiro',
    icon: 'trending_up',
    descricao: 'Restabelecer a relação encargo-remuneração afetada por fatos imprevisíveis',
  },
  {
    chave: 'REAJUSTE',
    titulo: 'Pedido de Reajuste',
    icon: 'percent',
    descricao: 'Aplicar o índice de correção previsto no contrato após 12 meses',
  },
  {
    chave: 'REPACTUACAO',
    titulo: 'Pedido de Repactuação',
    icon: 'groups',
    descricao: 'Adequar o contrato de serviços contínuos à nova Convenção Coletiva (CCT)',
  },
];

const DrLicitaChat: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ prompt?: string } | undefined>();
  const [chats, setChats] = useState<DrLicitaChatSummary[]>([]);
  const [activeChat, setActiveChat] = useState<DrLicitaChatSummary | null>(null);
  const [messages, setMessages] = useState<DrLicitaChatMessage[]>([]);
  const [options, setOptions] = useState<OpcoesPayload | null>(null);
  const [draft, setDraft] = useState<DraftPayload | null>(null);
  const [pecaSalva, setPecaSalva] = useState<PecaSalvaPayload | null>(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamSteps, setStreamSteps] = useState<PassoDoTurno[]>([]);
  const [streamText, setStreamText] = useState('');
  const [pendingMode, setPendingMode] = useState('peticao');
  const [escolhendoPeca, setEscolhendoPeca] = useState(false);
  const [draftCollapsed, setDraftCollapsed] = useState(false);
  const [moverChat, setMoverChat] = useState<number | null>(null);
  const [menuChat, setMenuChat] = useState<number | null>(null);
  const [menuCabecalho, setMenuCabecalho] = useState(false);
  const [renomearChat, setRenomearChat] = useState<DrLicitaChatSummary | null>(null);
  const [novoTitulo, setNovoTitulo] = useState('');
  const [projetos, setProjetos] = useState<DrLicitaProjeto[]>([]);
  const [aviso, setAviso] = useState<string | null>(null);
  const [confirmacao, setConfirmacao] = useState<{
    titulo: string;
    mensagem: string;
    acao: () => void;
  } | null>(null);
  // Painel da petição (modo Atendimento)
  const [canvasDoc, setCanvasDoc] = useState<CanvasDocument | null>(null);
  const [canvasLive, setCanvasLive] = useState<CanvasLive | null>(null);
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [canvasExpanded, setCanvasExpanded] = useState(false);
  const canvasRef = useRef<CanvasPanelHandle>(null);
  // Documento atual para o stream: o turno pode durar minutos e o estado
  // capturado quando ele começou já não é o vigente (salvamento do painel)
  const canvasDocRef = useRef<CanvasDocument | null>(null);
  canvasDocRef.current = canvasDoc;
  // Corpo do documento em stream: acumulado no ref e publicado no estado em
  // lotes (centenas de deltas por petição re-renderizariam o markdown inteiro)
  const canvasBufferRef = useRef('');
  const canvasFlushTimer = useRef<number | null>(null);
  // Turno do Atendimento: a conversa do stream em andamento NESTA aba e a
  // conversa cujo turno começou antes (página recarregada, troca de conversa)
  // e está sendo acompanhado pelo estado no servidor. Os eventos do stream só
  // mudam a tela se a conversa dele for a aberta.
  const [turnoChatId, setTurnoChatId] = useState<number | null>(null);
  const [esperandoChatId, setEsperandoChatId] = useState<number | null>(null);
  const [parando, setParando] = useState(false);
  // null = automático (recolhe com o painel aberto)
  const [sidebarAberta, setSidebarAberta] = useState<boolean | null>(null);
  const activeChatIdRef = useRef<number | null>(null);
  activeChatIdRef.current = activeChat?.id ?? null;
  const vivoRef = useRef<{
    chatId: number;
    texto: string;
    passos: PassoDoTurno[];
    canvas: CanvasLive | null;
  } | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);

  const mostrarAviso = (texto: string) => {
    setAviso(texto);
    window.setTimeout(() => setAviso(null), 3500);
  };

  // Rascunho novo/atualizado reabre o preview
  useEffect(() => {
    if (draft) setDraftCollapsed(false);
  }, [draft]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastAssistantRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-expande o campo conforme o texto (até o max-height do CSS)
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [input]);

  const profile = useSelector((state: any) => state?.storage?.profile?.profile);
  const firstName = profile?.name?.split(' ')?.[0];
  // Convite de feedback só durante o prazo do teste do embaixador.
  const ambassadorTool = isAmbassadorTesting(profile?.ambassador, DR_LICITA_AMBASSADOR_SLUG)
    ? findAmbassadorTool(DR_LICITA_AMBASSADOR_SLUG)
    : undefined;
  // O Atendimento tem flag própria no Rails (fora da lista de alfa testers)
  const atendimentoLiberado = Boolean(profile?.dr_licita_atendimento);
  // Modo da conversa de petição: o Atendimento para quem o tem, senão o V3
  const modoPeticao = atendimentoLiberado ? MODO_ATENDIMENTO : 'peticao';
  // Conversa criada pelo campo (sem escolher card) ou pelo anexo: petição
  // também vira Atendimento para quem o tem
  const modoDaConversaNova = pendingMode === 'peticao' ? modoPeticao : pendingMode;

  const dispatch = useDispatch();
  useEffect(() => {
    fetchDrLicitaChats().then(setChats);
    // O perfil guardado pode ser de antes de uma flag nova (ex.: o modo
    // Atendimento): quem chega direto aqui, sem passar pelo painel, recarrega
    dispatch(refreshProfile());
  }, [dispatch]);

  // Prompt vindo da Biblioteca: preenche o campo para o usuário ajustar
  // os marcadores e enviar (conversa nova no modo Dúvidas)
  useEffect(() => {
    const prompt = location.state?.prompt;
    if (!prompt) return;
    setInput(prompt);
    setPendingMode('duvidas');
    textareaRef.current?.focus();
    history.replace('/dr_licita/chat');
  }, []);

  // Auto-scroll: resposta do assistente ancora pelo início (para não
  // empurrar a saudação/resposta longa para fora da tela); mensagens do
  // usuário e a bolha "pensando" ancoram pelo fim
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!loading && last?.role === 'assistant' && lastAssistantRef.current) {
      lastAssistantRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, options, loading]);

  const limparPainel = () => {
    setCanvasDoc(null);
    setCanvasLive(null);
    setCanvasOpen(false);
    setCanvasExpanded(false);
  };

  const resetView = () => {
    setActiveChat(null);
    setMessages([]);
    setOptions(null);
    setDraft(null);
    setPecaSalva(null);
    setPendingMode('peticao');
    setEscolhendoPeca(false);
    limparPainel();
  };

  // Excluir do histórico: some a conversa; a peça salva segue em Petições
  const excluirConversa = (chatId: number) => {
    const temPeca = chats.find((chat) => chat.id === chatId)?.document_id;
    setConfirmacao({
      titulo: 'Excluir conversa',
      mensagem: temPeca
        ? 'Deseja excluir esta conversa? A petição salva não será afetada.'
        : 'Deseja excluir esta conversa?',
      acao: async () => {
        setConfirmacao(null);
        const ok = await deleteDrLicitaChat(chatId);
        if (!ok) return;
        setChats((previous) => previous.filter((chat) => chat.id !== chatId));
        if (activeChat?.id === chatId) resetView();
      },
    });
  };

  const abrirRenomear = (chat: DrLicitaChatSummary) => {
    setRenomearChat(chat);
    setNovoTitulo(tituloConversa(chat));
  };

  const salvarTitulo = async () => {
    const titulo = novoTitulo.trim();
    if (!renomearChat || !titulo) return;
    const ok = await renameDrLicitaChat(renomearChat.id, titulo);
    if (ok) {
      setChats((previous) =>
        previous.map((chat) => (chat.id === renomearChat.id ? { ...chat, title: titulo } : chat)),
      );
      if (activeChat?.id === renomearChat.id) setActiveChat({ ...activeChat, title: titulo });
    } else {
      mostrarAviso('Não foi possível renomear a conversa. Tente novamente.');
    }
    setRenomearChat(null);
  };

  // Mover conversa para um projeto (organização tipo ChatGPT/Claude)
  const abrirMover = (chatId: number) => {
    setMoverChat(chatId);
    fetchDrLicitaProjetos().then(setProjetos);
  };

  const moverPara = async (projectId: number | null) => {
    if (moverChat === null) return;
    const ok = await setChatProjeto(moverChat, projectId);
    if (ok) {
      setChats((previous) =>
        previous.map((chat) => (chat.id === moverChat ? { ...chat, project_id: projectId } : chat)),
      );
      const nome = projetos.find((projeto) => projeto.id === projectId)?.name;
      mostrarAviso(
        projectId
          ? `Conversa movida para o projeto “${nome}”.`
          : 'Conversa devolvida ao histórico geral.',
      );
    } else {
      mostrarAviso('Não foi possível mover a conversa. Tente novamente.');
    }
    setMoverChat(null);
  };

  const openChat = useCallback(async (chat: DrLicitaChatSummary) => {
    setLoading(true);
    const data = await fetchDrLicitaChatMessages(chat.id);
    setLoading(false);
    if (!data) return;

    setActiveChat(data.chat);
    setMessages(data.messages);
    setDraft(data.chat.current_draft || null);
    setOptions(null);
    setPecaSalva(null);
    setEscolhendoPeca(false);
    // Como no IAGov, o painel começa fechado ao reabrir a conversa: abre
    // pelo card do documento ou pelo botão do cabeçalho
    setCanvasOpen(false);
    setCanvasExpanded(false);
    const vivo = vivoRef.current;
    if (vivo && vivo.chatId === chat.id) {
      // Voltou à conversa cujo turno roda nesta aba: a escrita ao vivo reaparece
      setStreamSteps(vivo.passos);
      setStreamText(vivo.texto);
      setCanvasLive(vivo.canvas);
      if (vivo.canvas) setCanvasOpen(true);
    } else {
      setStreamSteps([]);
      setStreamText('');
      setCanvasLive(null);
      // Turno que começou antes (outra aba, página recarregada): espera por ele
      if (data.chat.turno?.em_andamento) setEsperandoChatId(chat.id);
    }
    setCanvasDoc(data.chat.mode === MODO_ATENDIMENTO ? await fetchCanvas(chat.id) : null);
  }, []);

  // Acompanha o turno que roda no servidor sem stream nesta aba: ao acabar,
  // a conversa é recarregada com a resposta e o painel gravados
  useEffect(() => {
    if (esperandoChatId === null) return undefined;
    const chatId = esperandoChatId;
    let ativo = true;
    const conferir = async () => {
      const estado = await fetchTurnoAtendimento(chatId);
      if (!ativo || !estado || estado.em_andamento) return;
      ativo = false;
      setEsperandoChatId(null);
      setParando(false);
      fetchDrLicitaChats().then(setChats);
      if (activeChatIdRef.current !== chatId) return;
      const data = await fetchDrLicitaChatMessages(chatId);
      if (data && activeChatIdRef.current === chatId) {
        setMessages(data.messages);
        setCanvasDoc(await fetchCanvas(chatId));
      }
    };
    const timer = window.setInterval(conferir, 3000);
    const aoVoltar = () => {
      if (window.document.visibilityState === 'visible') conferir();
    };
    window.document.addEventListener('visibilitychange', aoVoltar);
    return () => {
      ativo = false;
      window.clearInterval(timer);
      window.document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [esperandoChatId]);

  // Sair da página encerra só a leitura do stream: o turno segue no agente
  useEffect(() => () => streamAbortRef.current?.abort(), []);

  // Fechar o painel devolve a sidebar ao automático
  useEffect(() => {
    if (!canvasOpen) setSidebarAberta(null);
  }, [canvasOpen]);

  const publicarCorpo = () => {
    canvasFlushTimer.current = null;
    const vivo = vivoRef.current;
    if (vivo?.canvas) vivo.canvas = { ...vivo.canvas, content: canvasBufferRef.current };
    if (vivo && activeChatIdRef.current === vivo.chatId) setCanvasLive(vivo.canvas);
  };

  // Turno do modo Atendimento: chat e painel no mesmo stream. Sem o
  // fallback não-stream do V3: o turno é longo e repeti-lo dobraria custo.
  // Os eventos ficam presos à conversa de origem: se o usuário abrir outra
  // no meio, a resposta não cai nela (fica gravada na conversa certa).
  const deliverAtendimento = async (chatId: number, content: string) => {
    await canvasRef.current?.flush();
    setOptions(null);
    setMessages((previous) => [
      ...previous,
      { id: Date.now(), role: 'user', content, rating: null, created_at: '' },
    ]);
    const vivo = {
      chatId,
      texto: '',
      passos: [] as PassoDoTurno[],
      canvas: null as CanvasLive | null,
    };
    vivoRef.current = vivo;
    canvasBufferRef.current = '';
    setTurnoChatId(chatId);
    setStreamSteps([]);
    setStreamText('');
    const aqui = () => activeChatIdRef.current === chatId;
    const publicar = () => {
      if (!aqui()) return;
      setStreamSteps(vivo.passos);
      setStreamText(vivo.texto);
      setCanvasLive(vivo.canvas);
    };
    let erro: { mensagem: string; code?: string; id?: number } | null = null;
    const controle = new AbortController();
    streamAbortRef.current = controle;

    const response = await streamDrLicitaChatMessage(
      chatId,
      content,
      (event) => {
        if (
          (event.type === 'tool_start' && event.tool) ||
          (event.type === 'reasoning' && event.content)
        ) {
          const label =
            event.type === 'reasoning'
              ? event.content || ''
              : TOOL_STATUS[event.tool || ''] || 'Executando ferramentas…';
          if (!vivo.passos.some((step) => step.label === label)) {
            vivo.passos = [
              ...vivo.passos.map((step) => ({ ...step, done: true })),
              { label, done: false },
            ];
            publicar();
          }
        }
        if (event.type === 'tool_end') {
          vivo.passos = vivo.passos.map((step, index) =>
            index === vivo.passos.length - 1 ? { ...step, done: true } : step,
          );
          publicar();
        }
        if (event.type === 'delta' && event.content) {
          vivo.texto += event.content;
          if (aqui()) setStreamText(vivo.texto);
        }
        if (event.type === 'document_start') {
          canvasBufferRef.current = '';
          vivo.canvas = {
            title: event.title || 'Petição',
            content: '',
            streaming: true,
            updating: false,
          };
          if (aqui()) {
            setCanvasLive(vivo.canvas);
            setCanvasOpen(true);
          }
        }
        if (event.type === 'document_delta' && event.content) {
          canvasBufferRef.current += event.content;
          if (canvasFlushTimer.current === null) {
            canvasFlushTimer.current = window.setTimeout(publicarCorpo, 120);
          }
        }
        if (event.type === 'document_end') {
          publicarCorpo();
          if (vivo.canvas) vivo.canvas = { ...vivo.canvas, streaming: false };
          publicar();
        }
        if (event.type === 'document_updating') {
          vivo.canvas = {
            title: event.title || vivo.canvas?.title || canvasDocRef.current?.title || 'Petição',
            content:
              vivo.canvas?.content ||
              canvasDocRef.current?.content ||
              canvasBufferRef.current ||
              '',
            streaming: false,
            updating: true,
          };
          if (aqui()) {
            setCanvasOpen(true);
            setCanvasLive(vivo.canvas);
          }
        }
        if (event.type === 'document_updated') {
          vivo.canvas = null;
          if (aqui()) {
            if (event.document) setCanvasDoc(event.document);
            setCanvasLive(null);
            const edicoes = event.edits;
            if (edicoes?.nao_aplicadas) {
              mostrarAviso(
                edicoes.nao_aplicadas > 1
                  ? `${edicoes.nao_aplicadas} alterações não foram aplicadas no painel. Veja o aviso na conversa.`
                  : '1 alteração não foi aplicada no painel. Veja o aviso na conversa.',
              );
            } else if (edicoes?.aplicadas) {
              mostrarAviso(
                edicoes.aplicadas > 1
                  ? `${edicoes.aplicadas} alterações aplicadas no painel.`
                  : 'Alteração aplicada no painel.',
              );
            }
          }
        }
        if (event.type === 'error') {
          erro = {
            mensagem: event.error || FALHA_ATENDIMENTO,
            code: event.code,
            id: event.assistant_message_id,
          };
        }
      },
      controle.signal,
    );

    if (streamAbortRef.current === controle) streamAbortRef.current = null;
    if (canvasFlushTimer.current !== null) {
      window.clearTimeout(canvasFlushTimer.current);
      canvasFlushTimer.current = null;
    }
    vivoRef.current = null;
    setTurnoChatId(null);
    setParando(false);
    if (controle.signal.aborted) return;
    fetchDrLicitaChats().then(setChats);
    if (!aqui()) return;
    setStreamSteps([]);
    setStreamText('');
    setCanvasLive(null);

    const falha = erro as { mensagem: string; code?: string; id?: number } | null;
    if (!response) {
      if (falha?.code === 'turno_em_andamento') {
        // A conversa já tem um turno rodando (outra aba): a pergunta volta
        // ao campo e a tela espera a resposta que está saindo
        setMessages((previous) => previous.slice(0, -1));
        setInput(content);
        setEsperandoChatId(chatId);
        mostrarAviso(falha.mensagem);
        return;
      }
      if (!falha) {
        // Stream caiu sem terminal (rede, proxy): o turno pode seguir no agente
        const estado = await fetchTurnoAtendimento(chatId);
        if (estado?.em_andamento) {
          setEsperandoChatId(chatId);
          return;
        }
        const data = await fetchDrLicitaChatMessages(chatId);
        const ultima = data?.messages[data.messages.length - 1];
        if (data && ultima?.role === 'assistant' && !ultima.payload?.falha) {
          // A resposta foi gravada; só o fim do stream se perdeu
          setMessages(data.messages);
          setCanvasDoc(await fetchCanvas(chatId));
          return;
        }
      }
      setMessages((previous) => [
        ...previous,
        {
          id: falha?.id || Date.now() + 1,
          role: 'assistant',
          content: falha?.mensagem || FALHA_ATENDIMENTO,
          payload: { falha: true },
          rating: null,
          created_at: '',
          retry: content,
        },
      ]);
      return;
    }

    setMessages((previous) => [
      ...previous,
      {
        id: response.assistant_message_id || Date.now() + 1,
        role: 'assistant',
        content: response.content,
        payload: {
          ...(response.canvas ? { canvas: response.canvas } : {}),
          ...(response.interrompida ? { interrompida: true } : {}),
        },
        rating: null,
        created_at: '',
      },
    ]);
    if (response.canvas || response.canvas_edits) {
      // O documento salvo é a fonte da verdade (a edição leve é aplicada no servidor)
      const documento = await fetchCanvas(chatId);
      if (documento && aqui()) setCanvasDoc(documento);
    }
  };

  // Botão Parar: o agente grava o que já saiu e o stream termina em seguida
  const pararResposta = async () => {
    const chatId = activeChat?.id;
    if (!chatId || parando) return;
    setParando(true);
    const ok = await pararTurnoAtendimento(chatId);
    if (!ok) {
      setParando(false);
      mostrarAviso('A resposta já estava terminando.');
    }
  };

  const deliver = async (chatId: number, content: string, mode?: string) => {
    if (mode === MODO_ATENDIMENTO) {
      await deliverAtendimento(chatId, content);
      return;
    }
    setOptions(null);
    setMessages((previous) => [
      ...previous,
      { id: Date.now(), role: 'user', content, rating: null, created_at: '' },
    ]);
    setLoading(true);

    // Caminho preferencial: streaming com progresso ao vivo; qualquer falha
    // cai no request tradicional (fallback silencioso)
    setStreamSteps([]);
    setStreamText('');
    let response = await streamDrLicitaChatMessage(chatId, content, (event) => {
      if (event.type === 'tool_start' && event.tool) {
        const label = TOOL_STATUS[event.tool] || 'Executando ferramentas…';
        setStreamSteps((previous) => [...previous, { label, done: false }]);
      }
      if (event.type === 'tool_progress' && event.summary) {
        setStreamSteps((previous) =>
          previous.map((step, index) =>
            index === previous.length - 1 ? { ...step, detail: event.summary } : step,
          ),
        );
      }
      if (event.type === 'tool_end') {
        setStreamSteps((previous) =>
          previous.map((step, index) =>
            index === previous.length - 1 ? { ...step, done: true, detail: undefined } : step,
          ),
        );
      }
      if (event.type === 'delta' && event.content) {
        setStreamText((previous) => previous + event.content);
      }
    });
    if (!response) response = await sendDrLicitaChatMessage(chatId, content);

    setStreamSteps([]);
    setStreamText('');
    setLoading(false);
    if (!response) {
      setMessages((previous) => [
        ...previous,
        {
          id: Date.now() + 1,
          role: 'assistant',
          content: 'Tive um problema ao processar sua mensagem. Pode tentar novamente?',
          rating: null,
          created_at: '',
        },
      ]);
      return;
    }

    const reply = response;
    setMessages((previous) => [
      ...previous,
      {
        // Id persistido habilita a avaliação 👍/👎; sem ele, id sintético
        id: reply.assistant_message_id || Date.now() + 1,
        role: 'assistant',
        content: reply.content,
        rating: null,
        created_at: '',
      },
    ]);
    if (reply.opcoes) setOptions(reply.opcoes);
    if (reply.draft) setDraft(reply.draft);
    if (reply.peca_salva) {
      setPecaSalva(reply.peca_salva);
      fetchDrLicitaChats().then(setChats);
    }
  };

  const respondendo = turnoChatId !== null && turnoChatId === activeChat?.id;
  const esperando = esperandoChatId !== null && esperandoChatId === activeChat?.id;
  const ocupado = loading || respondendo || esperando;

  const send = async (contentOverride?: string) => {
    const content = (contentOverride ?? input).trim();
    if (!content || ocupado) return;
    if (turnoChatId !== null) {
      // Um turno por vez nesta aba: a tela ao vivo é uma só
      mostrarAviso('Aguarde o Dr. Licita terminar a resposta na outra conversa.');
      return;
    }
    setInput('');

    let chat = activeChat;
    if (!chat) {
      const created = await createDrLicitaChat(modoDaConversaNova);
      if (!created) {
        setMessages((previous) => [
          ...previous,
          { id: Date.now(), role: 'user', content, rating: null, created_at: '' },
          {
            id: Date.now() + 1,
            role: 'assistant',
            content:
              'Não consegui iniciar a conversa agora (o serviço do Dr. Licita parece indisponível). Tente novamente em instantes.',
            rating: null,
            created_at: '',
          },
        ]);
        return;
      }
      chat = created.chat;
      setActiveChat(chat);
      fetchDrLicitaChats().then(setChats);
    }
    await deliver(chat.id, content, chat.mode);
  };

  // "Tentar novamente" de uma falha: tira a bolha de erro e a pergunta
  // repetida antes dela (o envio a recoloca) e manda o mesmo texto
  const tentarDeNovo = (message: DrLicitaChatMessage, texto: string) => {
    if (ocupado) return;
    setMessages((previous) => {
      const indice = previous.findIndex((item) => item.id === message.id);
      return previous.filter(
        (item, idx) => idx !== indice && !(idx === indice - 1 && item.role === 'user'),
      );
    });
    send(texto);
  };

  // Resposta que não chegou (o turno morreu no meio, sem nem gravar a falha):
  // a última mensagem é a do usuário e nada está rodando
  const reenviarUltima = () => {
    const ultima = messages[messages.length - 1];
    if (!ultima || ultima.role !== 'user' || ocupado) return;
    setMessages((previous) => previous.slice(0, -1));
    send(ultima.content);
  };

  // "Pedir ao Dr. Licita" sobre o trecho selecionado no painel: o trecho vai
  // citado para o campo do chat, que é o único campo de pedido
  const pedirSobreTrecho = (trecho: string) => {
    const citado = trecho
      .trim()
      .split('\n')
      .map((linha) => `> ${linha}`)
      .join('\n');
    setInput(`Sobre este trecho da petição:\n${citado}\n\n`);
    window.setTimeout(() => {
      const campo = textareaRef.current;
      if (!campo) return;
      campo.focus();
      campo.setSelectionRange(campo.value.length, campo.value.length);
    }, 0);
  };

  // Avaliação 👍/👎: otimista, com reversão se a API falhar. Clicar na
  // avaliação já dada limpa. Ids sintéticos (Date.now, mensagens locais de
  // erro) não são avaliáveis — só mensagens persistidas no agente.
  const rateMessage = async (message: DrLicitaChatMessage, value: 1 | -1) => {
    const rating = message.rating === value ? null : value;
    const apply = (novo: number | null) =>
      setMessages((previous) =>
        previous.map((item) => (item.id === message.id ? { ...item, rating: novo } : item)),
      );
    apply(rating);
    const ok = await rateDrLicitaChatMessage(message.id, rating);
    if (!ok) apply(message.rating);
  };

  // Conversa nova já com a primeira interação do agente (kickoff): dúvidas
  // comuns no modo Dúvidas; roteiro da peça escolhida no modo Petição
  const iniciarConversa = async (mode: string, tipoPeca?: string) => {
    if (loading) return;
    setPendingMode(mode);
    setEscolhendoPeca(false);
    setActiveChat(null);
    setMessages([]);
    setOptions(null);
    setDraft(null);
    setPecaSalva(null);
    limparPainel();
    setLoading(true);

    const created = await createDrLicitaChat(mode, true, tipoPeca);
    setLoading(false);
    if (!created) {
      setMessages([
        {
          id: Date.now(),
          role: 'assistant',
          content:
            'Não consegui iniciar a conversa agora (o serviço do Dr. Licita parece indisponível). Tente novamente em instantes.',
          rating: null,
          created_at: '',
        },
      ]);
      return;
    }

    setActiveChat(created.chat);
    fetchDrLicitaChats().then(setChats);
    if (created.kickoff?.content) {
      setMessages([
        {
          id: created.kickoff.assistant_message_id || Date.now(),
          role: 'assistant',
          content: created.kickoff.content,
          rating: null,
          created_at: '',
        },
      ]);
      if (created.kickoff.opcoes) setOptions(created.kickoff.opcoes);
    }
    document.getElementById('dr-licita-chat-input')?.focus();
  };

  // Construir Petição abre a escolha do tipo de peça (o campo segue livre
  // para descrever o caso direto); os demais modos abrem a conversa
  const openMode = (mode: string) => {
    if (mode === 'jurisprudencia') {
      history.push('/dr_licita/jurisprudencia');
      return;
    }
    if (mode === 'peticao') {
      if (loading) return;
      resetView();
      setEscolhendoPeca(true);
      // O caso descrito direto no campo também vira conversa do Atendimento
      setPendingMode(modoPeticao);
      textareaRef.current?.focus();
      return;
    }
    iniciarConversa(mode);
  };

  // Deep-links de outras páginas do Dr. Licita (Petições, dashboard):
  // ?mode=peticao|duvidas abre a conversa já com o kickoff do modo;
  // ?chat=<id> reabre uma conversa do histórico
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    const chatId = Number(params.get('chat'));
    const caso = params.get('caso');
    // O acesso ao modo quem decide é o Rails (403 sem a flag): o perfil
    // guardado pode ainda não ter a flag quando a aba nova abre
    if (mode === MODO_ATENDIMENTO && caso) {
      // Vindo do Resumo IA da licitação: conversa nova com o caso no campo,
      // para o usuário conferir e enviar
      const texto = lerCasoDoResumo(caso);
      iniciarConversa(MODO_ATENDIMENTO).then(() => {
        if (texto) setInput(texto);
        textareaRef.current?.focus();
      });
      history.replace('/dr_licita/chat');
    } else if (mode === MODO_ATENDIMENTO) {
      iniciarConversa(MODO_ATENDIMENTO);
    } else if (mode && MODES.some((item) => item.mode === mode && !item.disabled)) {
      openMode(mode);
    } else if (chatId > 0) {
      openChat({ id: chatId } as DrLicitaChatSummary);
    }
  }, []);

  const attachFile = async (file: File) => {
    if (ocupado) return;
    setLoading(true);

    let chat = activeChat;
    if (!chat) {
      const created = await createDrLicitaChat(modoDaConversaNova);
      if (created) {
        chat = created.chat;
        setActiveChat(chat);
        fetchDrLicitaChats().then(setChats);
      }
    }
    if (!chat) {
      setLoading(false);
      return;
    }

    const result = await uploadDrLicitaChatAttachment(chat.id, file);
    setLoading(false);
    if (result.documento) {
      const { documento } = result;
      // Mensagem honesta sobre a leitura: edital longo é lido só até certa página
      let leitura = 'Documento recebido e lido por completo.';
      if (documento.truncado && documento.paginas_total && documento.pagina_corte) {
        leitura = `Documento recebido. Li até a página ${documento.pagina_corte} de ${documento.paginas_total}: o restante não foi lido. Se o ponto que importa estiver depois, anexe só esse trecho (o anexo ou os itens).`;
      } else if (documento.truncado) {
        leitura =
          'Documento recebido, mas lido só em parte (ele é longo). Se o ponto que importa estiver no fim, anexe só esse trecho.';
      } else if (documento.paginas_total) {
        leitura = `Documento recebido e lido por completo (${documento.paginas_total} página${
          documento.paginas_total > 1 ? 's' : ''
        }).`;
      }
      if (documento.paginas_sem_texto) {
        leitura += ` ${documento.paginas_sem_texto} página${
          documento.paginas_sem_texto > 1 ? 's são imagem' : ' é imagem'
        } sem texto e não pôde${documento.paginas_sem_texto > 1 ? 'ram' : ''} ser lida${
          documento.paginas_sem_texto > 1 ? 's' : ''
        }.`;
      }
      setMessages((previous) => [
        ...previous,
        {
          id: Date.now(),
          role: 'user',
          content: `[Anexou o documento: ${documento.nome}]`,
          rating: null,
          created_at: '',
        },
        {
          id: Date.now() + 1,
          role: 'assistant',
          content: `${leitura} Me conta o que você precisa a partir dele, por exemplo, qual item deseja questionar.`,
          rating: null,
          created_at: '',
        },
      ]);
    } else {
      setMessages((previous) => [
        ...previous,
        {
          id: Date.now(),
          role: 'assistant',
          content: `Não consegui ler o arquivo: ${result.error}`,
          rating: null,
          created_at: '',
        },
      ]);
    }
  };

  const renderDraftPanel = () => {
    if (!draft) return null;
    if (draftCollapsed) {
      return (
        <S.DraftRail>
          <button
            type="button"
            title="Mostrar preview do rascunho"
            onClick={() => setDraftCollapsed(false)}
          >
            <i className="material-icons">chevron_left</i>
          </button>
          <span>Preview do rascunho</span>
        </S.DraftRail>
      );
    }
    const { rascunho, verificacao } = draft;
    return (
      <S.DraftPanel>
        <S.DraftPanelHeader>
          <h3>Preview do rascunho</h3>
          <button type="button" title="Recolher preview" onClick={() => setDraftCollapsed(true)}>
            <i className="material-icons">chevron_right</i>
          </button>
        </S.DraftPanelHeader>
        <S.Verification ok={verificacao.aprovado}>
          {verificacao.aprovado
            ? 'Fundamentação verificada: todas as citações conferidas contra as teses do banco'
            : 'Verificação reprovada'}
        </S.Verification>
        <S.DraftSheet>
          <h4>{rascunho.titulo_peca}</h4>
          {rascunho.secoes.map((secao) => (
            <S.DraftSection key={secao.titulo}>
              <h5>{secao.titulo}</h5>
              <ReactMarkdown remarkPlugins={[gfm]}>{secao.texto}</ReactMarkdown>
              {(secao.fundamentos_literais || []).length > 0 && (
                <S.LiteralDetails>
                  <summary>
                    Fundamento literal — tese
                    {secao.teses_ids.length > 1 ? 's ' : ' '}
                    {secao.teses_ids.join(', ')}
                  </summary>
                  {(secao.fundamentos_literais || []).map((fundamento) => (
                    <S.Literal key={`${fundamento.tese_id}-${fundamento.trecho.slice(0, 24)}`}>
                      <b>Tese {fundamento.tese_id}</b>
                      {fundamento.trecho}
                    </S.Literal>
                  ))}
                </S.LiteralDetails>
              )}
            </S.DraftSection>
          ))}
        </S.DraftSheet>
      </S.DraftPanel>
    );
  };

  // Conversas movidas para projetos saem do histórico geral (vivem na
  // página do projeto; "Tirar do projeto" as devolve para cá)
  const historico = chats.filter((chat) => !chat.project_id);

  const showWelcome = !activeChat && messages.length === 0 && !loading;
  // O título muda no servidor depois do turno (primeira mensagem, depois a
  // petição): o cabeçalho segue a lista do histórico, que é recarregada
  const chatAtivo = (activeChat && chats.find((chat) => chat.id === activeChat.id)) || activeChat;
  const painelAberto = canvasOpen && activeChat?.mode === MODO_ATENDIMENTO;
  // Com o painel aberto a sidebar vira um trilho, para sobrar espaço ao chat
  const sidebarRecolhida = sidebarAberta === null ? painelAberto : !sidebarAberta;
  const ultimaMensagem = messages[messages.length - 1];
  // Pergunta sem resposta gravada e nada rodando: o turno morreu no meio
  const respostaPerdida =
    activeChat?.mode === MODO_ATENDIMENTO &&
    !ocupado &&
    turnoChatId === null &&
    ultimaMensagem?.role === 'user' &&
    ultimaMensagem.id < 1000000000 &&
    !ultimaMensagem.content.startsWith('[Anexou o documento:');
  const passosVisiveis =
    esperando && streamSteps.length === 0 ? [{ label: ESPERANDO_TURNO, done: false }] : streamSteps;
  // Atalhos de casos comuns só na conversa livre: a aberta pelo tipo de
  // petição ("Vamos preparar o seu recurso...") já tem o caso escolhido
  const mostrarCasosComuns =
    activeChat?.mode === MODO_ATENDIMENTO &&
    messages.length === 1 &&
    messages[0].role === 'assistant' &&
    !messages[0].content.startsWith('Vamos preparar') &&
    !ocupado &&
    !input.trim();

  return (
    <S.Page $bottomOffset={ambassadorTool ? AMBASSADOR_FEEDBACK_BAR_HEIGHT : 0}>
      {sidebarRecolhida ? (
        <S.SidebarRail>
          <button
            type="button"
            title="Mostrar o menu"
            aria-label="Mostrar o menu"
            onClick={() => setSidebarAberta(true)}
          >
            <i className="material-icons" aria-hidden="true">
              menu
            </i>
          </button>
          <button
            type="button"
            title="Nova conversa"
            aria-label="Nova conversa"
            onClick={resetView}
          >
            <i className="material-icons" aria-hidden="true">
              add
            </i>
          </button>
          <Link to="/dr_licita/peticoes" title="Petições" aria-label="Petições">
            <i className="material-icons" aria-hidden="true">
              description
            </i>
          </Link>
        </S.SidebarRail>
      ) : (
        <S.Sidebar>
          <h1>
            Dr.Licita
            {painelAberto && (
              <button
                type="button"
                title="Recolher o menu"
                aria-label="Recolher o menu"
                onClick={() => setSidebarAberta(false)}
              >
                <i className="material-icons" aria-hidden="true">
                  chevron_left
                </i>
              </button>
            )}
          </h1>
          <S.SidebarScroll data-clarity-mask="True">
            <S.SidebarButton onClick={resetView}>
              <i className="material-icons">add</i>
              Nova conversa
            </S.SidebarButton>
            <S.SidebarButton as={Link} to="/dr_licita/peticoes">
              <i className="material-icons">description</i>
              Petições
            </S.SidebarButton>
            <S.SidebarButton as={Link} to="/dr_licita/projetos">
              <i className="material-icons">folder_open</i>
              Projetos
            </S.SidebarButton>
            <S.SidebarButton as={Link} to="/dr_licita/biblioteca">
              <i className="material-icons">menu_book</i>
              Biblioteca de Prompts
            </S.SidebarButton>

            <h2>Acesso rápido</h2>
            {MODES.map((item) => (
              <S.SidebarButton
                key={item.mode}
                disabled={item.disabled}
                onClick={() => !item.disabled && openMode(item.mode)}
              >
                <i className="material-icons">{item.icon}</i>
                {item.title}
                {item.disabled && <small>em breve</small>}
              </S.SidebarButton>
            ))}

            {historico.length > 0 && <h2>Histórico</h2>}
            {historico.map((chat) => (
              <S.HistoryItem key={chat.id} active={activeChat?.id === chat.id}>
                <button type="button" title={tituloConversa(chat)} onClick={() => openChat(chat)}>
                  {tituloConversa(chat)}
                </button>
                <button
                  type="button"
                  title="Opções"
                  onClick={() => setMenuChat(menuChat === chat.id ? null : chat.id)}
                >
                  <i className="material-icons">more_horiz</i>
                </button>
                {menuChat === chat.id && (
                  <S.HistoryMenu>
                    <button
                      type="button"
                      onClick={() => {
                        setMenuChat(null);
                        abrirMover(chat.id);
                      }}
                    >
                      <i className="material-icons">drive_file_move</i>
                      Mover para projeto
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMenuChat(null);
                        abrirRenomear(chat);
                      }}
                    >
                      <i className="material-icons">edit</i>
                      Renomear
                    </button>
                    <button
                      type="button"
                      className="perigo"
                      onClick={() => {
                        setMenuChat(null);
                        excluirConversa(chat.id);
                      }}
                    >
                      <i className="material-icons">delete_outline</i>
                      Excluir
                    </button>
                  </S.HistoryMenu>
                )}
              </S.HistoryItem>
            ))}
          </S.SidebarScroll>
        </S.Sidebar>
      )}

      <S.Main>
        {showWelcome && (
          <S.Banner>
            Dr. Licita — a IA Jurídica do ConLicitação, com base de dados própria e teses reais
            validadas por advogados.
          </S.Banner>
        )}

        {showWelcome && escolhendoPeca && (
          <S.Landing>
            <h2>Construir Petição</h2>
            <p>Escolha o tipo de petição ou descreva o seu caso no campo abaixo.</p>
            <S.TipoCards>
              {TIPOS_PECA.map((tipo) => (
                <S.TipoCard
                  key={tipo.chave}
                  type="button"
                  onClick={() => iniciarConversa(modoPeticao, tipo.chave)}
                >
                  <S.ModeCardIcon>
                    <i className="material-icons">{tipo.icon}</i>
                  </S.ModeCardIcon>
                  <S.ModeCardBody>
                    <strong>
                      {tipo.titulo}
                      <i className="material-icons">arrow_right_alt</i>
                    </strong>
                    <span>{tipo.descricao}</span>
                  </S.ModeCardBody>
                </S.TipoCard>
              ))}
            </S.TipoCards>
          </S.Landing>
        )}

        {showWelcome && !escolhendoPeca && (
          <S.Welcome>
            <h2>{firstName ? `Olá, ${firstName}` : 'Olá!'}</h2>
            <p>Como o Dr. Licita pode apoiar você hoje?</p>
            <S.ModeCards>
              {MODES.map((item) => (
                <S.ModeCard
                  key={item.mode}
                  disabled={item.disabled}
                  onClick={() => !item.disabled && openMode(item.mode)}
                >
                  <S.ModeCardIcon>
                    <i className="material-icons">{item.icon}</i>
                  </S.ModeCardIcon>
                  <S.ModeCardBody>
                    <strong>
                      {item.title}
                      <i className="material-icons">arrow_right_alt</i>
                    </strong>
                    <span>
                      {item.description}
                      {item.disabled ? ' (em breve)' : ''}
                    </span>
                  </S.ModeCardBody>
                </S.ModeCard>
              ))}
            </S.ModeCards>
          </S.Welcome>
        )}

        {!showWelcome && (
          <S.ChatArea>
            <S.Conversation
              style={
                canvasOpen && canvasExpanded && activeChat?.mode === MODO_ATENDIMENTO
                  ? { display: 'none' }
                  : undefined
              }
            >
              {activeChat && (
                <S.ChatHeader>
                  <h3 title={tituloConversa(chatAtivo || activeChat)}>
                    {tituloConversa(chatAtivo || activeChat)}
                  </h3>
                  <button
                    type="button"
                    className="icone"
                    title="Renomear"
                    onClick={() => abrirRenomear(chatAtivo || activeChat)}
                  >
                    <i className="material-icons">edit</i>
                  </button>
                  {activeChat.mode === MODO_ATENDIMENTO &&
                    (canvasDoc || canvasLive) &&
                    !canvasOpen && (
                      <button
                        type="button"
                        className="icone"
                        title="Abrir o painel da petição"
                        onClick={() => setCanvasOpen(true)}
                      >
                        <i className="material-icons">description</i>
                      </button>
                    )}
                  <S.ChatHeaderMenu>
                    <button
                      type="button"
                      className="icone"
                      title="Opções"
                      onClick={() => setMenuCabecalho(!menuCabecalho)}
                    >
                      <i className="material-icons">more_vert</i>
                    </button>
                    {menuCabecalho && (
                      <S.HistoryMenu>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuCabecalho(false);
                            abrirMover(activeChat.id);
                          }}
                        >
                          <i className="material-icons">drive_file_move</i>
                          Mover para projeto
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuCabecalho(false);
                            abrirRenomear(chatAtivo || activeChat);
                          }}
                        >
                          <i className="material-icons">edit</i>
                          Renomear
                        </button>
                        <button
                          type="button"
                          className="perigo"
                          onClick={() => {
                            setMenuCabecalho(false);
                            excluirConversa(activeChat.id);
                          }}
                        >
                          <i className="material-icons">delete_outline</i>
                          Excluir
                        </button>
                      </S.HistoryMenu>
                    )}
                  </S.ChatHeaderMenu>
                </S.ChatHeader>
              )}
              <S.Messages data-clarity-mask="True">
                <S.MessagesInner>
                  {messages.map((message, index) =>
                    message.role === 'assistant' ? (
                      <S.AssistantRow
                        key={message.id}
                        ref={index === messages.length - 1 ? lastAssistantRef : undefined}
                      >
                        <S.ThinkingAvatar>
                          <i className="material-icons">balance</i>
                        </S.ThinkingAvatar>
                        <S.AssistantBubble>
                          <header>
                            DR. LICITA
                            <em>●</em>
                            ASSISTENTE
                          </header>
                          <ReactMarkdown remarkPlugins={[gfm]}>{message.content}</ReactMarkdown>
                          {(() => {
                            // Falha do turno: reenviar o mesmo pedido sem redigitar
                            const anterior = messages[index - 1];
                            const texto =
                              message.retry ||
                              (message.payload?.falha &&
                              index === messages.length - 1 &&
                              anterior?.role === 'user'
                                ? anterior.content
                                : undefined);
                            if (!texto || ocupado) return null;
                            return (
                              <S.RetryButton
                                type="button"
                                onClick={() => tentarDeNovo(message, texto)}
                              >
                                <i className="material-icons" aria-hidden="true">
                                  refresh
                                </i>
                                Tentar novamente
                              </S.RetryButton>
                            );
                          })()}
                          {message.payload?.canvas && (
                            <C.DocumentCard type="button" onClick={() => setCanvasOpen(true)}>
                              <i className="material-icons">description</i>
                              <div>
                                <strong>{message.payload.canvas.title}</strong>
                                <span>
                                  Petição no painel · versão {message.payload.canvas.version} ·
                                  abrir
                                </span>
                              </div>
                            </C.DocumentCard>
                          )}
                          {message.id < 1000000000 && !message.payload?.falha && (
                            <S.RatingRow>
                              <button
                                type="button"
                                title="Resposta útil"
                                className={message.rating === 1 ? 'active' : ''}
                                onClick={() => rateMessage(message, 1)}
                              >
                                <i className="material-icons">
                                  {message.rating === 1 ? 'thumb_up' : 'thumb_up_off_alt'}
                                </i>
                              </button>
                              <button
                                type="button"
                                title="Resposta ruim"
                                className={message.rating === -1 ? 'active' : ''}
                                onClick={() => rateMessage(message, -1)}
                              >
                                <i className="material-icons">
                                  {message.rating === -1 ? 'thumb_down' : 'thumb_down_off_alt'}
                                </i>
                              </button>
                            </S.RatingRow>
                          )}
                        </S.AssistantBubble>
                      </S.AssistantRow>
                    ) : (
                      <S.Bubble key={message.id} role={message.role}>
                        <ReactMarkdown remarkPlugins={[gfm]}>{message.content}</ReactMarkdown>
                      </S.Bubble>
                    ),
                  )}
                  {respostaPerdida && (
                    <S.LostReply>
                      A resposta a esta mensagem não foi concluída.
                      <S.RetryButton type="button" onClick={reenviarUltima}>
                        <i className="material-icons" aria-hidden="true">
                          refresh
                        </i>
                        Tentar novamente
                      </S.RetryButton>
                    </S.LostReply>
                  )}
                  {mostrarCasosComuns && (
                    <S.CasosComuns aria-label="Casos comuns">
                      {CASOS_COMUNS.map((caso) => (
                        <button
                          key={caso.rotulo}
                          type="button"
                          onClick={() => {
                            setInput(caso.texto);
                            textareaRef.current?.focus();
                          }}
                        >
                          {caso.rotulo}
                        </button>
                      ))}
                    </S.CasosComuns>
                  )}
                  {options && !ocupado && (
                    <S.OptionsBlock>
                      {options.pergunta && <strong>{options.pergunta}</strong>}
                      {options.opcoes.map((texto, index) => (
                        <S.OptionButton key={texto} onClick={() => send(texto)}>
                          <b>{index + 1}</b>
                          {texto}
                        </S.OptionButton>
                      ))}
                    </S.OptionsBlock>
                  )}
                  {pecaSalva && (
                    <S.SavedNotice>
                      Petição “{pecaSalva.titulo}” {pecaSalva.atualizada ? 'atualizada' : 'salva'}{' '}
                      em Petições.
                      <Link to={`/dr_licita/peticoes?document=${pecaSalva.id}`}>
                        Abrir o documento final
                      </Link>
                    </S.SavedNotice>
                  )}
                  {ocupado && streamText === '' && (
                    <S.Thinking>
                      <S.ThinkingAvatar>
                        <i className="material-icons">balance</i>
                      </S.ThinkingAvatar>
                      <S.ThinkingBubble>
                        <header>
                          DR. LICITA
                          <em>●</em>
                          PENSANDO
                        </header>
                        {passosVisiveis.length > 0 && (
                          <S.ThinkingSteps>
                            {passosVisiveis.map((step, index) => (
                              // Etapas repetem rótulo (ex.: 2 buscas) — índice compõe a chave
                              // eslint-disable-next-line react/no-array-index-key
                              <li key={`${step.label}-${index}`}>
                                {step.done ? (
                                  <i className="material-icons">check</i>
                                ) : (
                                  <S.StepSpinner />
                                )}
                                <span>{step.detail || step.label}</span>
                              </li>
                            ))}
                          </S.ThinkingSteps>
                        )}
                        <S.ThinkingDots>
                          <span />
                          <span />
                          <span />
                        </S.ThinkingDots>
                      </S.ThinkingBubble>
                    </S.Thinking>
                  )}
                  {ocupado && streamText !== '' && (
                    <S.AssistantRow>
                      <S.ThinkingAvatar>
                        <i className="material-icons">balance</i>
                      </S.ThinkingAvatar>
                      <S.AssistantBubble>
                        <header>
                          DR. LICITA
                          <em>●</em>
                          ASSISTENTE
                        </header>
                        <ReactMarkdown remarkPlugins={[gfm]}>{streamText}</ReactMarkdown>
                        {(canvasLive?.streaming || canvasLive?.updating) && (
                          <C.DocumentCard type="button" onClick={() => setCanvasOpen(true)}>
                            <i className="material-icons">edit_note</i>
                            <div>
                              <strong>{canvasLive.title}</strong>
                              <span>
                                {canvasLive.streaming
                                  ? 'Escrevendo a petição no painel…'
                                  : 'Atualizando a petição no painel…'}
                              </span>
                            </div>
                          </C.DocumentCard>
                        )}
                      </S.AssistantBubble>
                    </S.AssistantRow>
                  )}
                  <div ref={messagesEndRef} />
                </S.MessagesInner>
              </S.Messages>
            </S.Conversation>
            {activeChat?.mode === MODO_ATENDIMENTO
              ? canvasOpen && (
                  <CanvasPanel
                    ref={canvasRef}
                    chatId={activeChat.id}
                    document={canvasDoc}
                    live={canvasLive}
                    busy={respondendo || esperando}
                    expanded={canvasExpanded}
                    onToggleExpand={() => setCanvasExpanded(!canvasExpanded)}
                    onClose={() => {
                      setCanvasOpen(false);
                      setCanvasExpanded(false);
                    }}
                    onDocumentChange={setCanvasDoc}
                    onNotice={mostrarAviso}
                    onAskAboutSelection={pedirSobreTrecho}
                  />
                )
              : renderDraftPanel()}
          </S.ChatArea>
        )}

        <S.InputRegion>
          <S.InputBar
            onSubmit={(event) => {
              event.preventDefault();
              send();
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt"
              style={{ display: 'none' }}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) attachFile(file);
                event.target.value = '';
              }}
            />
            <S.AttachButton
              type="button"
              title="Anexar edital ou documento (PDF/DOCX/TXT)"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="material-icons">add</i>
            </S.AttachButton>
            <textarea
              id="dr-licita-chat-input"
              data-clarity-mask="True"
              aria-label="Mensagem para o Dr. Licita"
              ref={textareaRef}
              rows={1}
              value={input}
              placeholder={
                escolhendoPeca
                  ? 'Ou descreva o seu caso: o que aconteceu e o que você precisa'
                  : activeChat?.mode === MODO_ATENDIMENTO && canvasDoc
                  ? 'Peça ajustes na petição ou continue a conversa'
                  : 'Pergunte ao Dr. Licita'
              }
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  send();
                }
              }}
            />
            {activeChat?.mode === MODO_ATENDIMENTO && (respondendo || esperando) ? (
              <button
                type="button"
                className="parar"
                disabled={parando}
                onClick={pararResposta}
                title={parando ? 'Parando…' : 'Parar a resposta'}
                aria-label={parando ? 'Parando a resposta' : 'Parar a resposta'}
              >
                <i className="material-icons" aria-hidden="true">
                  {parando ? 'hourglass_empty' : 'stop'}
                </i>
              </button>
            ) : (
              <button
                type="submit"
                disabled={ocupado || !input.trim()}
                title="Enviar"
                aria-label="Enviar"
              >
                <i className="material-icons" aria-hidden="true">
                  send
                </i>
              </button>
            )}
          </S.InputBar>
        </S.InputRegion>
        <S.Disclaimer>
          O Dr. Licita pode cometer erros. Confirme informações relevantes antes de agir.
        </S.Disclaimer>
      </S.Main>
      {moverChat !== null && (
        <Modal show centered onHide={() => setMoverChat(null)}>
          <PS.ModalContainer>
            <div className="modal-header">
              <h5 className="modal-title text-nowrap">
                <strong>Mover para projeto</strong>
              </h5>
              <button
                aria-label="Close"
                className="close"
                data-dismiss="modal"
                type="button"
                onClick={() => setMoverChat(null)}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <PS.ModalBody>
              {projetos.map((projeto) => (
                <S.ProjectOption
                  key={projeto.id}
                  type="button"
                  onClick={() => moverPara(projeto.id)}
                >
                  <i className="material-icons">folder</i>
                  {projeto.name}
                </S.ProjectOption>
              ))}
              {chats.find((chat) => chat.id === moverChat)?.project_id && (
                <S.ProjectOption type="button" onClick={() => moverPara(null)}>
                  <i className="material-icons">drive_file_move_rtl</i>
                  Tirar do projeto (histórico geral)
                </S.ProjectOption>
              )}
              {projetos.length === 0 && (
                <p>
                  Você ainda não tem projetos — crie um na página{' '}
                  <Link to="/dr_licita/projetos">Projetos</Link>.
                </p>
              )}
            </PS.ModalBody>
          </PS.ModalContainer>
        </Modal>
      )}
      {renomearChat && (
        <Modal show centered onHide={() => setRenomearChat(null)}>
          <PS.ModalContainer>
            <div className="modal-header">
              <h5 className="modal-title text-nowrap">
                <strong>Renomear</strong>
              </h5>
              <button
                aria-label="Close"
                className="close"
                data-dismiss="modal"
                type="button"
                onClick={() => setRenomearChat(null)}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <PS.ModalBody>
              <label htmlFor="chat-renomear-input">
                Nome
                <input
                  id="chat-renomear-input"
                  type="text"
                  value={novoTitulo}
                  ref={(element) => element?.focus()}
                  onChange={(event) => setNovoTitulo(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') salvarTitulo();
                  }}
                />
              </label>
            </PS.ModalBody>
            <div className="modal-footer justify-content-center">
              <button type="button" className="btn btn-sm btn-primary" onClick={salvarTitulo}>
                Salvar
              </button>
            </div>
          </PS.ModalContainer>
        </Modal>
      )}
      {(menuChat !== null || menuCabecalho) && (
        <S.MenuOverlay
          aria-label="Fechar menu"
          onClick={() => {
            setMenuChat(null);
            setMenuCabecalho(false);
          }}
        />
      )}
      {confirmacao && (
        <DrLicitaConfirm
          titulo={confirmacao.titulo}
          mensagem={confirmacao.mensagem}
          onCancelar={() => setConfirmacao(null)}
          onConfirmar={confirmacao.acao}
        />
      )}
      {aviso && <S.Toast>{aviso}</S.Toast>}
      {ambassadorTool && <AmbassadorFeedbackBar toolSlug={ambassadorTool.slug} />}
    </S.Page>
  );
};

export default DrLicitaChat;
